import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { AnalyticsMetrics } from "@/services";
import { useUser } from "@/contexts";
import { db } from "@/services/config";
import { doc, updateDoc, serverTimestamp } from "firebase/firestore";
import * as geofire from "geofire-common";

// Função auxiliar para calcular distância simples (Haversine) para controle de atualização
function getDistanceInKm(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number,
) {
  const R = 6371;
  const dLat = (lat2 - lat1) * (Math.PI / 180);
  const dLon = (lon2 - lon1) * (Math.PI / 180);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * (Math.PI / 180)) *
      Math.cos(lat2 * (Math.PI / 180)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

export function AnalyticsInterceptor() {
  const location = useLocation();
  const { user } = useUser();
  const lastUpdateCoords = useRef<{ lat: number; lng: number } | null>(null);
  const watchId = useRef<number | null>(null);

  useEffect(() => {
    AnalyticsMetrics.trackPageView(location.pathname + location.search);
  }, [location.pathname, location.search]);

  useEffect(() => {
    // 1. Verificação de suporte e autenticação
    if (!("geolocation" in navigator) || !user?.uid) return;

    // 2. Processamento de Coordenadas (Sem "IFs" de bloqueio de precisão)
    const handleLocationChange = async (position: GeolocationPosition) => {
      const { latitude, longitude, accuracy } = position.coords;

      // Cálculo de distância para evitar gravar no banco se o usuário estiver parado
      const distanceMoved = lastUpdateCoords.current
        ? getDistanceInKm(
            lastUpdateCoords.current.lat,
            lastUpdateCoords.current.lng,
            latitude,
            longitude,
          )
        : 999;

      // Threshold de 100 metros para otimizar gravações no Firestore
      if (distanceMoved > 0.1) {
        try {
          const hash = geofire.geohashForLocation([latitude, longitude]);
          const userRef = doc(db, "users", user.uid);

          await updateDoc(userRef, {
            location: {
              geohash: hash,
              geopoint: { lat: latitude, lng: longitude },
              accuracy: accuracy, // Salva a margem de erro (ex: 5m ou 500m)
            },
            lastLocationUpdate: serverTimestamp(),
          });

          lastUpdateCoords.current = { lat: latitude, lng: longitude };
          // console.log(
          //   `✅ Localização Sincronizada: ${latitude}, ${longitude} (Precisão: ${accuracy}m)`,
          // );
        } catch (error) {
          console.error("Erro ao atualizar Firestore:", error);
        }
      }
    };

    const handleError = (error: GeolocationPositionError) => {
      console.error("Falha na localização:", error.message);
      AnalyticsMetrics.trackError("GeolocationError", error.message);
    };

    // 3. Configuração de Alta Performance
    const geoOptions: PositionOptions = {
      enableHighAccuracy: true, // ESSENCIAL: Força o hardware (GPS no celular / Wi-Fi no Desktop)
      maximumAge: 0, // Não aceita dados antigos (Cache)
      timeout: 30000, // 30 segundos para garantir que o sensor "fixe" a posição
    };

    // Inicia o monitoramento em tempo real
    watchId.current = navigator.geolocation.watchPosition(
      handleLocationChange,
      handleError,
      geoOptions,
    );

    // Cleanup para evitar vazamento de memória e gasto de bateria
    return () => {
      if (watchId.current !== null) {
        navigator.geolocation.clearWatch(watchId.current);
      }
    };
  }, [user?.uid]);

  return null;
}
